/* AQUASCAPE: lightbox for the "Video testimonial grid" section.

   The theme ships no reusable modal. PhotoSwipe v4 is present but its video
   handling in custom.js is wired specifically to the DIY gallery, so this
   builds its own small overlay rather than adding a branch to that shared file.

   Everything is delegated off `document`, so theme-editor section reloads,
   Shopify section rendering API swaps, and multiple sections on one page all
   work with no per-section init step. */
(function () {
  'use strict';

  // This file is pulled in by a <script> tag that lives inside the section, so
  // anything that re-inserts the section markup can run it a second time. A
  // second run would append a second overlay to <body> and bind a second
  // delegated click listener, so one click would open two stacked lightboxes
  // and each would need its own close. Only the first run owns the page.
  if (window.aqvtLightboxReady) return;
  window.aqvtLightboxReady = true;

  var OPEN_SELECTOR = '[data-aqvt-open]';
  var SECTION_SELECTOR = '[data-aqvt]';

  var lb = null; // the shared overlay, built lazily on first open
  var items = []; // trigger buttons belonging to the currently open section
  var index = 0;
  var lastFocused = null;

  function svg(paths, filled) {
    return (
      '<svg viewBox="0 0 24 24" fill="' +
      (filled ? 'currentColor' : 'none') +
      '" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
      'stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      paths +
      '</svg>'
    );
  }

  function build() {
    // build() appends to <body>, which a section re-render never touches, so an
    // overlay from an earlier run of an older copy of this file can still be
    // sitting there. Its listeners belong to a dead closure, so drop it.
    var stale = document.querySelectorAll('.aqvt-lb');
    for (var i = 0; i < stale.length; i++) {
      stale[i].parentNode.removeChild(stale[i]);
    }

    var el = document.createElement('div');
    el.className = 'aqvt-lb';
    el.setAttribute('hidden', '');
    el.innerHTML =
      '<button type="button" class="aqvt-lb__backdrop" data-aqvt-close tabindex="-1" aria-hidden="true"></button>' +
      '<div class="aqvt-lb__frame" role="dialog" aria-modal="true" aria-label="Video testimonial">' +
      '<button type="button" class="aqvt-lb__btn aqvt-lb__close" data-aqvt-close aria-label="Close video">' +
      svg('<path d="M18 6 6 18M6 6l12 12"/>') +
      '</button>' +
      '<div class="aqvt-lb__body">' +
      '<button type="button" class="aqvt-lb__btn aqvt-lb__nav aqvt-lb__nav--prev" data-aqvt-prev aria-label="Previous video">' +
      svg('<path d="M15 18 9 12l6-6"/>') +
      '</button>' +
      '<div class="aqvt-lb__media" data-aqvt-stage></div>' +
      '<button type="button" class="aqvt-lb__btn aqvt-lb__nav aqvt-lb__nav--next" data-aqvt-next aria-label="Next video">' +
      svg('<path d="m9 18 6-6-6-6"/>') +
      '</button>' +
      '</div>' +
      '<p class="aqvt-lb__caption" data-aqvt-caption></p>' +
      '</div>';

    document.body.appendChild(el);

    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-aqvt-close]')) {
        close();
      } else if (e.target.closest('[data-aqvt-prev]')) {
        go(index - 1);
      } else if (e.target.closest('[data-aqvt-next]')) {
        go(index + 1);
      }
    });

    // Keep Tab inside the dialog while it is open.
    el.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusable = el.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), video[controls], iframe, [href]'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    return el;
  }

  function stopMedia() {
    if (!lb) return;
    var stage = lb.querySelector('[data-aqvt-stage]');
    // Removing the node is what actually stops playback: pausing a <video> is
    // not enough for an <iframe>, whose audio keeps running until it is gone.
    stage.innerHTML = '';
  }

  function ratioFor(trigger) {
    var section = trigger.closest(SECTION_SELECTOR);
    if (!section) return '9 / 16';
    return getComputedStyle(section).getPropertyValue('--aqvt-ratio').trim() || '9 / 16';
  }

  function render() {
    var trigger = items[index];
    if (!trigger) return;

    var card = trigger.closest('.aqvt__card');
    var tpl = card && card.querySelector('template[data-aqvt-media]');
    var stage = lb.querySelector('[data-aqvt-stage]');

    stopMedia();
    lb.style.setProperty('--aqvt-lb-ratio', ratioFor(trigger));

    if (tpl) {
      stage.appendChild(tpl.content.cloneNode(true));
      var video = stage.querySelector('video');
      if (video) {
        // The click that got us here is a user gesture, so sound is allowed.
        // If a browser still refuses, leave the poster and controls in place.
        var attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch(function () {});
        }
      }
    }

    var name = trigger.getAttribute('data-aqvt-name') || '';
    var role = trigger.getAttribute('data-aqvt-role') || '';
    var caption = lb.querySelector('[data-aqvt-caption]');
    var html = '';
    if (name) html += '<span class="aqvt-lb__caption-name">' + name + '</span>';
    if (role) html += '<span class="aqvt-lb__caption-role">' + role + '</span>';
    if (items.length > 1) {
      html +=
        '<span class="aqvt-lb__counter">' + (index + 1) + ' / ' + items.length + '</span>';
    }
    caption.innerHTML = html;

    var prev = lb.querySelector('[data-aqvt-prev]');
    var next = lb.querySelector('[data-aqvt-next]');
    var multiple = items.length > 1;
    prev.hidden = !multiple;
    next.hidden = !multiple;
    prev.disabled = !multiple;
    next.disabled = !multiple;
  }

  function go(next) {
    if (!items.length) return;
    // Wrap around, so the arrows never dead-end.
    index = (next + items.length) % items.length;
    render();
  }

  function open(trigger) {
    var section = trigger.closest(SECTION_SELECTOR);
    if (!section) return;

    items = Array.prototype.slice.call(section.querySelectorAll(OPEN_SELECTOR));
    index = items.indexOf(trigger);
    if (index < 0) index = 0;

    lastFocused = document.activeElement;
    if (!lb) lb = build();

    lb.hidden = false;
    document.documentElement.classList.add('aqvt-lb-open');
    render();

    // Next frame, so the opening transition has a state to animate from.
    requestAnimationFrame(function () {
      lb.classList.add('is-open');
      var close = lb.querySelector('.aqvt-lb__close');
      if (close) close.focus();
    });
  }

  function close() {
    if (!lb || lb.hidden) return;
    stopMedia();
    lb.classList.remove('is-open');
    lb.hidden = true;
    document.documentElement.classList.remove('aqvt-lb-open');
    items = [];
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest(OPEN_SELECTOR);
    if (!trigger) return;
    e.preventDefault();
    open(trigger);
  });

  document.addEventListener('keydown', function (e) {
    if (!lb || lb.hidden) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowLeft' && items.length > 1) {
      go(index - 1);
    } else if (e.key === 'ArrowRight' && items.length > 1) {
      go(index + 1);
    }
  });

  // Theme editor: a section reload can destroy the card the lightbox is
  // showing, leaving an overlay pinned over nothing.
  document.addEventListener('shopify:section:unload', close);
  document.addEventListener('shopify:section:select', close);
})();

/* Dots for the mobile swipe row.

   Separate IIFE with its own guard: the lightbox above owns page-level state
   and bails on a second run, but dots are per-section and a theme-editor
   reload hands us brand new nodes that still need binding.

   The row is a native scroll-snap flex row, not Flickity, so the active dot
   follows scrollLeft rather than a carousel API. */
(function () {
  'use strict';

  var BOUND = 'aqvtDotsBound';

  function offsets(items) {
    // Positions relative to the first card, which is what scrollLeft counts
    // from. offsetLeft on its own is measured from the offset parent.
    var base = items[0].offsetLeft;
    var out = [];
    for (var i = 0; i < items.length; i++) out.push(items[i].offsetLeft - base);
    return out;
  }

  function nearest(list, x) {
    var best = 0;
    var bestDistance = Infinity;
    for (var i = 0; i < list.length; i++) {
      var d = Math.abs(list[i] - x);
      if (d < bestDistance) {
        bestDistance = d;
        best = i;
      }
    }
    return best;
  }

  function bind(nav) {
    if (nav.dataset[BOUND]) return;
    var section = nav.closest('[data-aqvt]');
    var grid = section && section.querySelector('.aqvt__grid');
    var dots = Array.prototype.slice.call(nav.querySelectorAll('[data-aqvt-dot]'));
    var items = grid ? Array.prototype.slice.call(grid.children) : [];
    if (!grid || dots.length === 0 || items.length === 0) return;
    nav.dataset[BOUND] = '1';

    var active = -1;

    function paint(i) {
      if (i === active) return;
      active = i;
      for (var d = 0; d < dots.length; d++) {
        dots[d].classList.toggle('is-active', d === i);
      }
    }

    var queued = false;
    grid.addEventListener(
      'scroll',
      function () {
        // scroll fires per frame on touch; coalesce so we read layout once.
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          paint(nearest(offsets(items), grid.scrollLeft));
        });
      },
      { passive: true }
    );

    nav.addEventListener('click', function (e) {
      var dot = e.target.closest('[data-aqvt-dot]');
      if (!dot) return;
      var i = parseInt(dot.getAttribute('data-aqvt-dot'), 10) || 0;
      if (!items[i]) return;
      var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      grid.scrollTo({
        left: offsets(items)[i],
        behavior: smooth ? 'smooth' : 'auto'
      });
      paint(i);
    });

    paint(nearest(offsets(items), grid.scrollLeft));
  }

  function scan(root) {
    var navs = (root || document).querySelectorAll('[data-aqvt-dots]');
    for (var i = 0; i < navs.length; i++) bind(navs[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
})();
